import React from "react";
import { Box, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";

import type { CriteriaDirection, ThresholdUnit } from "../TargetBuilderPanel.types";

interface CriteriaRowProps {
  consecutivePoints: number | "";
  thresholdValue: number | "";
  direction: CriteriaDirection;
  /** Outcome text shown after the threshold box, e.g. "→ advance to next phase". */
  outcomeText: string;
  onConsecutiveChange: (value: number | "") => void;
  onThresholdChange: (value: number | "") => void;
  onDirectionChange?: (value: CriteriaDirection) => void;
  thresholdSuffix?: string;
  thresholdAriaLabel?: string;
  thresholdMin?: number;
  thresholdMax?: number;
  thresholdStep?: number;
  thresholdUnit?: ThresholdUnit | "";
  thresholdUnitOptions?: Array<{ value: ThresholdUnit; label: string }>;
  onThresholdUnitChange?: (unit: ThresholdUnit) => void;
  /** When true, the direction control is rendered as plain text (auto-set). */
  lockDirection?: boolean;
  /** Optional ids/names for Formik error wiring. */
  consecutiveError?: string | false;
  thresholdError?: string | false;
}

const normalizeThresholdInput = (
  value: number,
  min: number,
  max?: number,
  step?: number
): number => {
  let next = value;

  if (typeof step === "number" && step > 0) {
    next = Math.round(next / step) * step;
  }

  if (typeof min === "number") next = Math.max(min, next);
  if (typeof max === "number") next = Math.min(max, next);

  return next;
};

/**
 * "[N] consecutive data points above/below [P]% → outcome"
 * Used for mastery, regression, baseline progression, and generalization rows.
 */
const CriteriaRow: React.FC<CriteriaRowProps> = ({
  consecutivePoints,
  thresholdValue,
  direction,
  outcomeText,
  onConsecutiveChange,
  onThresholdChange,
  onDirectionChange,
  thresholdSuffix = "%",
  thresholdAriaLabel = "Threshold value",
  thresholdMin = 0,
  thresholdMax,
  thresholdStep,
  thresholdUnit,
  thresholdUnitOptions = [],
  onThresholdUnitChange,
  lockDirection = false,
  consecutiveError,
  thresholdError,
}) => (
  <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ rowGap: 1 }}>
    <Box sx={{ width: 72, flexShrink: 0 }}>
      <TextField
        fullWidth
        size="small"
        type="number"
        value={consecutivePoints}
        onChange={(e) => {
          const v = e.target.value;
          onConsecutiveChange(v === "" ? "" : Number(v));
        }}
        error={Boolean(consecutiveError)}
        helperText={consecutiveError || undefined}
        inputProps={{
          min: 1,
          "aria-label": "Consecutive data points",
        }}
        sx={{ "& input": { textAlign: "center", padding: "8px 6px" } }}
      />
    </Box>

    {lockDirection || !onDirectionChange ? (
      // Locked: render "consecutive data points <direction>" as a single inline string
      <Typography variant="body2">consecutive data points {direction}</Typography>
    ) : (
      <>
        <Typography variant="body2">consecutive data points</Typography>
        <Box sx={{ width: 100, flexShrink: 0 }}>
          <Select
            fullWidth
            size="small"
            value={direction}
            onChange={(e) => onDirectionChange(e.target.value as CriteriaDirection)}
          >
            <MenuItem value="above">above</MenuItem>
            <MenuItem value="below">below</MenuItem>
          </Select>
        </Box>
      </>
    )}

    <Box sx={{ width: 72, flexShrink: 0 }}>
      <TextField
        fullWidth
        size="small"
        type="number"
        value={thresholdValue}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "") {
            onThresholdChange("");
            return;
          }

          const parsed = Number(v);
          if (Number.isNaN(parsed)) {
            onThresholdChange("");
            return;
          }

          onThresholdChange(
            normalizeThresholdInput(parsed, thresholdMin, thresholdMax, thresholdStep)
          );
        }}
        error={Boolean(thresholdError)}
        helperText={thresholdError || undefined}
        inputProps={{
          min: thresholdMin,
          ...(typeof thresholdMax === "number" ? { max: thresholdMax } : {}),
          ...(typeof thresholdStep === "number" ? { step: thresholdStep } : {}),
          "aria-label": thresholdAriaLabel,
        }}
        sx={{ "& input": { textAlign: "center", padding: "8px 6px" } }}
      />
    </Box>

    {thresholdUnitOptions.length > 1 && onThresholdUnitChange && (
      <Box sx={{ width: 90, flexShrink: 0 }}>
        <Select
          fullWidth
          size="small"
          value={thresholdUnit || "seconds"}
          onChange={(e) => onThresholdUnitChange(e.target.value as ThresholdUnit)}
          inputProps={{ "aria-label": "Threshold unit" }}
        >
          {thresholdUnitOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    )}

    <Typography variant="body2">{`${thresholdSuffix} ${outcomeText}`}</Typography>
  </Stack>
);

export default CriteriaRow;
